import { NEWS_ARMS, NEWS_UNAVAILABLE_NOTICE } from '../../core/ruleRegistry';
// Param editors for trading-restriction rule types.

import NumberInput from '../NumberInput';
import Dropdown from '../Dropdown';
import { TIMEZONE_OPTIONS, type RuleBuilderState } from '../../core/ruleRegistry';

interface ParamProps {
  rule: RuleBuilderState;
  onUpdateParams: (params: Record<string, unknown>) => void;
}

function paramStr(rule: RuleBuilderState, key: string, fallback = ''): string {
  return (rule.params[key] as string) ?? fallback;
}

function paramNum(rule: RuleBuilderState, key: string, fallback = ''): string {
  const v = rule.params[key];
  return v != null ? String(v) : fallback;
}

/** The arm every required picker opens on: one the author has to leave. */
const CHOOSE = { value: '', label: 'Choose\u2026' } as const;

/** One term of the news window, read back out of the typed object. */
function windowTerm(rule: RuleBuilderState, key: string): unknown {
  const window = rule.params['window'];
  return window && typeof window === 'object'
    ? (window as Record<string, unknown>)[key]
    : undefined;
}

/** Trading Hours: start, end, timezone */
export function TradingHoursParams(props: ParamProps) {
  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Start (HH:MM)</span>
        <input
          class="ch-input"
          type="text"
          value={paramStr(props.rule, 'start', '09:00')}
          onInput={(e) =>
            props.onUpdateParams({ ...props.rule.params, start: e.currentTarget.value })
          }
          placeholder="09:00"
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">End (HH:MM)</span>
        <input
          class="ch-input"
          type="text"
          value={paramStr(props.rule, 'end', '17:00')}
          onInput={(e) =>
            props.onUpdateParams({ ...props.rule.params, end: e.currentTarget.value })
          }
          placeholder="17:00"
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Timezone</span>
        <Dropdown
          value={paramStr(props.rule, 'timezone')}
          options={[
            CHOOSE,
            ...TIMEZONE_OPTIONS.map((zone) => ({ value: zone.value, label: zone.label })),
          ]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, timezone: v || undefined })
          }
        />
      </div>
    </div>
  );
}

/** Weekend Holding: close_before_minutes, session_scope */
export function WeekendHoldingParams(props: ParamProps) {
  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Close Before (minutes)</span>
        <NumberInput
          value={paramNum(props.rule, 'close_before_minutes', '5')}
          onInput={(v) =>
            props.onUpdateParams({
              ...props.rule.params,
              close_before_minutes: v ? Number(v) : 5,
            })
          }
          min={0}
          step={1}
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Session Scope</span>
        <Dropdown
          value={paramStr(props.rule, 'session_scope', 'regular')}
          options={[
            { value: 'regular', label: 'Regular (RTH)' },
            { value: 'extended', label: 'Extended (ETH)' },
            { value: 'none', label: 'None' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, session_scope: v })
          }
        />
      </div>
    </div>
  );
}

/**
 * Flat by Schedule: boundary + its own keys, the lead, and the blackout.
 *
 * The two boundaries read DIFFERENT keys and the compiler refuses a key its
 * boundary never reads, so switching the boundary replaces the whole param set
 * rather than merging into it — a `min_closed_minutes` left behind on a daily
 * row is a publish the author cannot explain.
 */
export function FlatByScheduleParams(props: ParamProps) {
  const boundary = () => paramStr(props.rule, 'boundary', 'daily_time');
  const cutoffTime = () => paramStr(props.rule, 'latest_cutoff_time', '');

  /**
   * A daily-time window ends `blackout_after_minutes` after its own deadline,
   * so zero is an EMPTY window — the rule would flatten and permit the re-open
   * in the same instant — and the compiler and the params CHECK both refuse it.
   * A session-close window ends at the market's reopen, so zero is a legitimate
   * "no tail".
   */
  const minBlackout = () => (boundary() === 'daily_time' ? 1 : 0);

  /**
   * The wall-clock backstop a session-close row may carry: the deadline is the
   * EARLIER of the closure's lead-adjusted instant and this cutoff.
   *
   * OPTIONAL, and both of its keys move together — the compiler refuses a
   * cutoff time with no zone, and an empty time removes the pair rather than
   * publishing a key the boundary would then have to explain.
   */
  function setCutoffTime(time: string) {
    const next = { ...props.rule.params };
    delete next.latest_cutoff_time;
    delete next.latest_cutoff_tz;
    if (time.trim() !== '') {
      next.latest_cutoff_time = time;
      next.latest_cutoff_tz = paramStr(props.rule, 'latest_cutoff_tz', 'America/Chicago');
    }
    props.onUpdateParams(next);
  }

  function selectBoundary(next: string) {
    const carried = Number(paramNum(props.rule, 'blackout_after_minutes', '5'));
    const common = {
      boundary: next,
      close_before_minutes: Number(paramNum(props.rule, 'close_before_minutes', '0')),
      // A blackout of zero carried off a session row would be an EMPTY daily
      // window, which the publish gate refuses by name.
      blackout_after_minutes:
        next === 'daily_time' ? Math.max(carried, 1) : carried,
    };
    props.onUpdateParams(
      next === 'session_close'
        ? { ...common, session_scope: 'none', min_closed_minutes: 120 }
        : next === 'before_calendar_event'
          ? { ...common, close_before_minutes: 5, blackout_after_minutes: Math.max(carried, 5), impacts: ['high'], currencies: ['USD'] }
          : { ...common, time: '16:55', tz: 'America/New_York' },
    );
  }

  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Deadline</span>
        <Dropdown
          value={boundary()}
          options={[
            { value: 'daily_time', label: 'A daily time' },
            { value: 'session_close', label: "The instrument's session close" },
            { value: 'before_calendar_event', label: 'Before a calendar event' },
          ]}
          onChange={selectBoundary}
        />
      </div>
      {boundary() === 'daily_time' ? (
        <>
          <div class="ch-field ch-field-sm">
            <span class="ch-label">Time (HH:MM)</span>
            <input
              class="ch-input"
              type="text"
              value={paramStr(props.rule, 'time', '16:55')}
              onInput={(e) =>
                props.onUpdateParams({ ...props.rule.params, time: e.currentTarget.value })
              }
              placeholder="16:55"
            />
          </div>
          <div class="ch-field ch-field-sm">
            <span class="ch-label">Timezone</span>
            <Dropdown
              value={paramStr(props.rule, 'tz', 'America/New_York')}
              options={TIMEZONE_OPTIONS.map((tz) => ({ value: tz.value, label: tz.label }))}
              onChange={(v) => props.onUpdateParams({ ...props.rule.params, tz: v })}
            />
          </div>
        </>
      ) : boundary() === 'before_calendar_event' ? (
        <>
          <div class="ch-field ch-field-sm">
            <span class="ch-label">Currencies (comma separated)</span>
            <input class="ch-input" value={Array.isArray(props.rule.params.currencies) ? props.rule.params.currencies.join(', ') : 'USD'}
              onInput={(e) => props.onUpdateParams({ ...props.rule.params, currencies: e.currentTarget.value.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) })} />
          </div>
          <div class="ch-field ch-field-sm">
            <span class="ch-label">Impacts (comma separated)</span>
            <input class="ch-input" value={Array.isArray(props.rule.params.impacts) ? props.rule.params.impacts.join(', ') : 'high'}
              onInput={(e) => props.onUpdateParams({ ...props.rule.params, impacts: e.currentTarget.value.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean) })} />
          </div>
        </>
      ) : (
        <>
          <div class="ch-field ch-field-sm">
            <span class="ch-label">Session Scope</span>
            <Dropdown
              value={paramStr(props.rule, 'session_scope', 'none')}
              options={[
                { value: 'regular', label: 'Regular (RTH)' },
                { value: 'extended', label: 'Extended (ETH)' },
                { value: 'none', label: 'None' },
              ]}
              onChange={(v) =>
                props.onUpdateParams({ ...props.rule.params, session_scope: v })
              }
            />
          </div>
          <div class="ch-field ch-field-sm">
            <span class="ch-label">Closed for at least (minutes)</span>
            <NumberInput
              value={paramNum(props.rule, 'min_closed_minutes', '120')}
              onInput={(v) =>
                props.onUpdateParams({
                  ...props.rule.params,
                  min_closed_minutes: v ? Number(v) : 120,
                })
              }
              min={1}
              step={1}
            />
          </div>
          <div class="ch-field ch-field-sm">
            <span class="ch-label">Latest Cutoff (HH:MM, optional)</span>
            <input
              class="ch-input"
              type="text"
              value={cutoffTime()}
              onInput={(e) => setCutoffTime(e.currentTarget.value)}
              placeholder="15:10"
            />
          </div>
          {cutoffTime() !== '' ? (
            <div class="ch-field ch-field-sm">
              <span class="ch-label">Cutoff Timezone</span>
              <Dropdown
                value={paramStr(props.rule, 'latest_cutoff_tz', 'America/Chicago')}
                options={TIMEZONE_OPTIONS.map((tz) => ({ value: tz.value, label: tz.label }))}
                onChange={(v) =>
                  props.onUpdateParams({ ...props.rule.params, latest_cutoff_tz: v })
                }
              />
            </div>
          ) : null}
        </>
      )}
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Flatten Before (minutes)</span>
        <NumberInput
          value={paramNum(props.rule, 'close_before_minutes', '0')}
          onInput={(v) =>
            props.onUpdateParams({
              ...props.rule.params,
              close_before_minutes: v ? Number(v) : 0,
            })
          }
          min={0}
          step={1}
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Blackout After (minutes)</span>
        <NumberInput
          value={paramNum(props.rule, 'blackout_after_minutes', '5')}
          onInput={(v) =>
            props.onUpdateParams({
              ...props.rule.params,
              blackout_after_minutes: v ? Number(v) : minBlackout(),
            })
          }
          min={minBlackout()}
          step={1}
        />
      </div>
    </div>
  );
}

/**
 * News Trading: the WINDOW and the ARM.
 *
 * Both are terms of the typed declaration, and the old editor collected
 * neither: it wrote `minutes_before`, `minutes_after` and `impact` beside each
 * other, which is the three-field-bag shape `EventWindow` replaced. A rule
 * authored that way could not be saved at all.
 *
 * The four arms are FOUR RULES, not four settings on one — each carries its own
 * ladder — so the arm is picked here and the picker opens unset.
 */
export function NewsTradingParams(props: ParamProps) {
  const impacts = (): string[] => {
    const stated = windowTerm(props.rule, 'impacts');
    return Array.isArray(stated) ? (stated as string[]) : [];
  };

  /** The terms the author has actually stated, and nothing else.
   *
   * NO AUTHORED DEFAULTS. Filling the three terms the author had not reached
   * yet — `currencies: 'instrument'`, and two zero margins — wrote a complete
   * window they never chose: a blackout of the event's own minute, scoped to a
   * currency set nobody picked, saved as if it had been sold. The window stays
   * PARTIAL until all four are stated, and `missingRuleTerms` refuses the save
   * by name until then. */
  function setWindow(next: Record<string, unknown>) {
    const stated = props.rule.params.window;
    const held =
      typeof stated === 'object' && stated !== null
        ? (stated as Record<string, unknown>)
        : {};
    props.onUpdateParams({
      ...props.rule.params,
      window: { ...held, ...next },
    });
  }

  function toggleImpact(level: string) {
    const current = impacts();
    setWindow({
      impacts: current.includes(level)
        ? current.filter((i) => i !== level)
        : [...current, level],
    });
  }

  /** A number field the author has not filled states NOTHING, rather than
   *  zero: a zero margin is a blackout of the event's own minute, which is a
   *  term somebody has to choose. */
  function setMinutes(term: string, raw: string) {
    if (raw.trim() === '') {
      const stated = props.rule.params.window;
      const held =
        typeof stated === 'object' && stated !== null
          ? { ...(stated as Record<string, unknown>) }
          : {};
      delete held[term];
      props.onUpdateParams({ ...props.rule.params, window: held });
      return;
    }
    setWindow({ [term]: Number(raw) });
  }

  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        {/* WHICH RELATION TO THE WINDOW THIS RULE JUDGES. Opening inside it and
            holding through it are different things to have done, and a firm
            prices them differently. */}
        <p>{NEWS_UNAVAILABLE_NOTICE}</p>
        <span class="ch-label">Arm</span>
        <Dropdown
          value={paramStr(props.rule, 'arm')}
          options={[CHOOSE, ...NEWS_ARMS]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, arm: v || undefined })
          }
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Currencies</span>
        <Dropdown
          value={String(windowTerm(props.rule, 'currencies') ?? '')}
          options={[
            CHOOSE,
            // THE ONE AUTHORABLE VALUE. The decoder's vocabulary is the
            // instrument's own currencies or an explicitly LISTED set, and
            // there is no editor for a listed set — so "Any currency" was a
            // term the server refuses with `unknown variant "any"`, offered to
            // an author as if it were a product.
            { value: 'instrument', label: "The instrument's own" },
          ]}
          onChange={(v) => {
            if (v === '') {
              const next = { ...props.rule.params };
              delete next.window;
              props.onUpdateParams(next);
              return;
            }
            setWindow({ currencies: v });
          }}
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Minutes Before</span>
        <NumberInput
          value={String(windowTerm(props.rule, 'before_minutes') ?? '')}
          onInput={(v) => setMinutes('before_minutes', v)}
          min={0}
          step={5}
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Minutes After</span>
        <NumberInput
          value={String(windowTerm(props.rule, 'after_minutes') ?? '')}
          onInput={(v) => setMinutes('after_minutes', v)}
          min={0}
          step={5}
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Impact Levels</span>
        <div class="ch-impact-checks">
          {(['high', 'medium', 'low'] as const).map((level) => (
            <label class="ch-rule-checkbox">
              <input
                type="checkbox"
                checked={impacts().includes(level)}
                onChange={() => toggleImpact(level)}
              />
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Instrument Whitelist/Blacklist: symbols (comma-separated) */
export function InstrumentListParams(props: ParamProps) {
  const symbolsStr = () => {
    const raw = props.rule.params.symbols;
    if (Array.isArray(raw)) return (raw as string[]).join(', ');
    return '';
  };

  function handleInput(value: string) {
    const symbols = value
      .split(/[,\s]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    props.onUpdateParams({ ...props.rule.params, symbols });
  }

  return (
    <div class="ch-rule-params-row">
      <div class="ch-field" style={{ flex: 1 }}>
        <span class="ch-label">Symbols (comma-separated)</span>
        <input
          class="ch-input"
          type="text"
          value={symbolsStr()}
          onInput={(e) => handleInput(e.currentTarget.value)}
          placeholder="EURUSD, GBPUSD, USDJPY"
        />
      </div>
    </div>
  );
}
